import { StandardEvents } from '@shopify/events';

class IffalconVariantSpecifications extends HTMLElement {
  connectedCallback() {
    this.renderStoredJsonSpecifications();
    this.buildSpecificationTabs();
    this.filterForSku(this.dataset.initialSku || '');
    document.addEventListener(StandardEvents.productSelect, this.handleProductSelect, true);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.handleProductSelect, true);
  }

  handleProductSelect = (event) => {
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        const sku = detail?.resource?.sku || '';
        if (sku) this.filterForSku(sku);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[iffalcon-specifications] Variant update failed:', error);
        }
      });
  };

  renderStoredJsonSpecifications() {
    this.querySelectorAll('.metafield-rich_text_field').forEach((container) => {
      const rawValue = container.textContent?.trim() || '';
      if (!rawValue.startsWith('{') || !rawValue.includes('"type"')) return;

      const specification = this.parseStoredJson(rawValue);
      if (!specification || specification.type !== 'root' || !Array.isArray(specification.children)) return;

      const content = document.createDocumentFragment();
      specification.children.forEach((node) => {
        const renderedNode = this.renderRichTextNode(node);
        if (renderedNode) content.append(renderedNode);
      });

      if (content.childNodes.length) {
        container.replaceChildren(content);
        container.classList.add('iffalcon-rendered-specification');
      }
    });
  }

  parseStoredJson(rawValue) {
    try {
      return JSON.parse(rawValue);
    } catch {
      // Shopify's Rich text editor can insert literal line breaks inside pasted
      // JSON string values. Replace only those control characters before parsing.
      let normalized = '';
      let insideString = false;
      let escaped = false;

      for (const character of rawValue) {
        if (insideString && (character === '\n' || character === '\r' || character === '\t')) {
          normalized += ' ';
          continue;
        }

        normalized += character;

        if (escaped) {
          escaped = false;
        } else if (character === '\\' && insideString) {
          escaped = true;
        } else if (character === '"') {
          insideString = !insideString;
        }
      }

      try {
        return JSON.parse(normalized);
      } catch (error) {
        console.warn('[iffalcon-specifications] Stored specification JSON is invalid:', error);
        return null;
      }
    }
  }

  renderRichTextNode(node) {
    if (!node || typeof node !== 'object') return null;

    if (node.type === 'text') {
      let renderedText = document.createTextNode(String(node.value || '').replace(/\s+/g, ' '));

      if (node.bold) {
        const strong = document.createElement('strong');
        strong.append(renderedText);
        renderedText = strong;
      }
      if (node.italic) {
        const emphasis = document.createElement('em');
        emphasis.append(renderedText);
        renderedText = emphasis;
      }
      return renderedText;
    }

    const elementByType = {
      paragraph: 'p',
      heading: `h${Math.min(Math.max(Number(node.level) || 3, 2), 6)}`,
      list: node.listType === 'ordered' ? 'ol' : 'ul',
      'list-item': 'li',
      link: 'a',
    };
    const tagName = elementByType[node.type];
    if (!tagName) return null;

    const element = document.createElement(tagName);
    if (node.type === 'link' && typeof node.url === 'string') {
      element.href = node.url;
    }

    if (Array.isArray(node.children)) {
      node.children.forEach((child) => {
        const renderedChild = this.renderRichTextNode(child);
        if (renderedChild) element.append(renderedChild);
      });
    }

    return element;
  }

  buildSpecificationTabs() {
    this.querySelectorAll('.metafield-rich_text_field').forEach((container, containerIndex) => {
      if (container.dataset.specificationTabsInitialized === 'true') return;

      const categoryHeadings = Array.from(container.querySelectorAll(':scope > h4'));
      if (categoryHeadings.length < 2) return;

      container.dataset.specificationTabsInitialized = 'true';
      container.querySelectorAll(':scope > h3').forEach((heading) => heading.remove());

      const tabList = document.createElement('div');
      tabList.className = 'iffalcon-spec-tabs';
      tabList.setAttribute('role', 'tablist');
      tabList.setAttribute('aria-label', 'Specification categories');

      const tabPanels = [];
      const tabButtons = [];
      const panelContainer = document.createElement('div');
      panelContainer.className = 'iffalcon-spec-tab-panels';

      categoryHeadings.forEach((heading, tabIndex) => {
        const label = heading.textContent?.trim().replace(/\s+/g, ' ') || `Category ${tabIndex + 1}`;
        const idBase = `iffalcon-spec-${containerIndex}-${tabIndex}`;
        const button = document.createElement('button');
        const panel = document.createElement('div');

        button.type = 'button';
        button.className = 'iffalcon-spec-tab';
        button.id = `${idBase}-tab`;
        button.textContent = label;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', `${idBase}-panel`);
        button.setAttribute('aria-selected', tabIndex === 0 ? 'true' : 'false');
        button.tabIndex = tabIndex === 0 ? 0 : -1;

        panel.className = 'iffalcon-spec-tab-panel';
        panel.id = `${idBase}-panel`;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', button.id);
        panel.hidden = tabIndex !== 0;

        let sibling = heading.nextElementSibling;
        while (sibling && sibling.tagName !== 'H4') {
          const nextSibling = sibling.nextElementSibling;
          panel.append(sibling);
          sibling = nextSibling;
        }
        heading.remove();
        this.formatSpecificationRows(panel);

        tabList.append(button);
        panelContainer.append(panel);
        tabButtons.push(button);
        tabPanels.push(panel);
      });

      const selectTab = (selectedIndex, focus = false) => {
        tabButtons.forEach((button, index) => {
          const isSelected = index === selectedIndex;
          button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          button.tabIndex = isSelected ? 0 : -1;
          tabPanels[index].hidden = !isSelected;
        });
        if (focus) tabButtons[selectedIndex].focus();
      };

      tabButtons.forEach((button, index) => {
        button.addEventListener('click', () => selectTab(index));
        button.addEventListener('keydown', (event) => {
          let nextIndex = null;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabButtons.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabButtons.length) % tabButtons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabButtons.length - 1;
          if (nextIndex === null) return;
          event.preventDefault();
          selectTab(nextIndex, true);
        });
      });

      container.prepend(panelContainer);
      container.prepend(tabList);
    });
  }

  formatSpecificationRows(panel) {
    panel.querySelectorAll(':scope > p').forEach((paragraph) => {
      paragraph.classList.add('iffalcon-spec-row');
      const strong = paragraph.querySelector(':scope > strong');
      if (!strong) {
        paragraph.classList.add('iffalcon-spec-row--full');
        return;
      }

      const label = document.createElement('span');
      const value = document.createElement('span');
      label.className = 'iffalcon-spec-row__label';
      value.className = 'iffalcon-spec-row__value';
      label.textContent = (strong.textContent || '').trim().replace(/\s+/g, ' ').replace(/:\s*$/, '');

      Array.from(paragraph.childNodes).forEach((node) => {
        if (node !== strong) value.append(node);
      });
      strong.remove();
      paragraph.replaceChildren(label, value);
    });
  }

  filterForSku(sku) {
    const normalizedSku = sku.trim().toLowerCase();
    const variantPanels = Array.from(this.querySelectorAll('[data-variant-sku]'));
    const legacyPanels = Array.from(this.querySelectorAll('details'));

    document.querySelectorAll('[data-iffalcon-product-sku]').forEach((skuElement) => {
      skuElement.textContent = sku ? `SKU: ${sku}` : '';
    });

    const activeSku = document.querySelector('[data-iffalcon-active-sku]');
    if (activeSku && sku) activeSku.textContent = sku;

    if (variantPanels.length) {
      let hasVariantMatch = false;
      variantPanels.forEach((panel) => {
        const panelSku = (panel.dataset.variantSku || '').trim().toLowerCase();
        const matches = normalizedSku && panelSku === normalizedSku;
        panel.hidden = !matches;
        if (matches) hasVariantMatch = true;
      });

      if (!hasVariantMatch) {
        variantPanels.forEach((panel) => {
          panel.hidden = false;
        });
      }
      return;
    }

    if (!legacyPanels.length) return;

    let matchedPanel = null;
    legacyPanels.forEach((panel) => {
      const summary = panel.querySelector('summary')?.textContent?.trim().toLowerCase() || '';
      const matches = normalizedSku && summary.startsWith(normalizedSku);
      panel.hidden = !matches;
      panel.open = matches;
      if (matches) matchedPanel = panel;
    });

    if (!matchedPanel) {
      legacyPanels.forEach((panel) => {
        panel.hidden = false;
        panel.open = false;
      });
    }
  }
}

if (!customElements.get('iffalcon-variant-specifications')) {
  customElements.define('iffalcon-variant-specifications', IffalconVariantSpecifications);
}
